/**
 * Historical FX → EUR lookup for the Finance import wizard.
 *
 * Non-EUR accounts must store an EUR amount per transaction (design §7). When a
 * bank export has no EUR column — e.g. a GBP Monzo export — the wizard can fetch
 * the official ECB daily reference rate for each transaction's date and convert
 * the native amount itself, so the user doesn't have to add a column by hand.
 *
 * Source: Frankfurter (https://frankfurter.dev) — free, keyless, CORS-enabled,
 * ECB end-of-day reference rates. Rates exist for TARGET business days only;
 * weekends and holidays reuse the most recent prior business-day rate (the same
 * value-dating banks use). A single time-series request covers the whole import
 * date range, so one fetch converts an entire file.
 */

export interface EurRateLookup {
  currency: string;
  /** EUR per 1 unit of `currency` for the given ISO date, or null if unknown. */
  rateFor(isoDate: string): number | null;
}

const HOST = 'https://api.frankfurter.dev/v1';

/** Shift an ISO date (YYYY-MM-DD) back by `days`, so the earliest transaction
 *  still resolves to a prior business-day rate even on a weekend/holiday. */
function isoMinusDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
  dt.setUTCDate(dt.getUTCDate() - days);
  return dt.toISOString().slice(0, 10);
}

/**
 * Fetch ECB daily rates converting `currency` → EUR across `[startISO, endISO]`
 * and return a lookup that fills weekend/holiday gaps with the most recent prior
 * rate. EUR resolves to a constant rate of 1 without a network call.
 *
 * Throws on a failed request or an empty result — the caller falls back to
 * asking the user to map an explicit EUR column.
 */
export async function fetchEurRates(
  currency: string,
  startISO: string,
  endISO: string,
): Promise<EurRateLookup> {
  const cur = currency.toUpperCase();
  if (cur === 'EUR') return { currency: cur, rateFor: () => 1 };

  // Pad the start so a first date on a weekend/holiday still finds a prior rate.
  const start = isoMinusDays(startISO, 7);
  const url = `${HOST}/${start}..${endISO}?base=${encodeURIComponent(cur)}&symbols=EUR`;

  let res: Response;
  try {
    res = await fetch(url);
  } catch {
    throw new Error('Could not reach the exchange-rate service. Map an EUR column instead.');
  }
  if (!res.ok) {
    throw new Error(`Exchange-rate lookup failed (HTTP ${res.status}). Map an EUR column instead.`);
  }

  const data = (await res.json()) as { rates?: Record<string, { EUR?: number }> };
  const entries = Object.entries(data.rates ?? {})
    .map(([date, r]) => [date, r?.EUR] as const)
    .filter((e): e is readonly [string, number] => typeof e[1] === 'number')
    .sort((a, b) => (a[0] < b[0] ? -1 : 1));

  if (entries.length === 0) {
    throw new Error(`No exchange rates available for ${cur}. Map an EUR column instead.`);
  }

  return {
    currency: cur,
    rateFor(isoDate: string): number | null {
      // The most recent rate on or before the date; fall back to the earliest
      // known rate when the date precedes the whole series.
      let best: number | null = null;
      for (const [date, rate] of entries) {
        if (date <= isoDate) best = rate;
        else break;
      }
      return best ?? entries[0]![1];
    },
  };
}
